import { useParams, Link, Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { accountService, type License } from '@/services/account-service';
import { useCurrentUser } from '@/hooks/use-current-user';
import { 
  Header, 
  Breadcrumb, 
  BreadcrumbList, 
  BreadcrumbItem, 
  BreadcrumbLink, 
  BreadcrumbSeparator, 
  BreadcrumbPage,
  ContentContainer,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Alert,
  AlertTitle,
  AlertDescription,
  Empty,
  EmptyIcon,
  EmptyTitle,
  EmptyDescription,
  Badge,
  Progress,
  Spinner
} from 'prosci-ui';
import { usePageContext } from '@/contexts/page-context';
import {
  AlertCircle,
  Info,
  User,
  Mail,
  Phone,
  Clock,
  CheckCircle,
  XCircle
} from '@/lib/icons';
import { format } from 'date-fns';

export function AccountDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { setShowPageHeader } = usePageContext();
  const { data: currentAdmin } = useCurrentUser();

  // Affiliate users should not have access to Accounts
  const isAffiliate = currentAdmin?.type === 'prosci_admin' && currentAdmin?.level === 'affiliate';

  useEffect(() => {
    setShowPageHeader(false);
    return () => setShowPageHeader(true);
  }, [setShowPageHeader]);

  // Redirect affiliate users to Users page
  if (isAffiliate) {
    return <Navigate to="/users" replace />;
  }

  // Fetch account details
  const { data, isLoading, error } = useQuery({
    queryKey: ['account', id],
    queryFn: () => accountService.getAccount(id!),
    enabled: !!id,
  });

  const account = data?.data?.attributes;

  if (isLoading) {
    return (
      <ContentContainer>
        <Empty>
          <EmptyIcon>
            <Spinner size="small" />
          </EmptyIcon>
          <EmptyTitle>Loading account details...</EmptyTitle>
        </Empty>
      </ContentContainer>
    );
  }

  if (error || !account) {
    return (
      <ContentContainer>
        <Alert variant="destructive">
          <AlertCircle />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>Failed to load account details. Please try again.</AlertDescription>
        </Alert>
      </ContentContainer>
    );
  }

  const activeLicenses = account.licenses?.filter(l => 
    l.active && (!l.usage_end_date || new Date(l.usage_end_date) >= new Date())
  ) || [];
  
  const currentLicenses = activeLicenses.filter(l => !l.is_legacy);
  const legacyLicenses = activeLicenses.filter(l => l.is_legacy);
  const hasActiveLicenses = activeLicenses.length > 0;

  return (
    <>
      <Header 
        title={account.name}
        size="small"
        breadcrumb={
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/accounts">Accounts</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>{account.name}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        }
      />
      <ContentContainer>
        {/* Info message */}
        <Alert variant="neutral">
          <Info />
          <AlertTitle>Information</AlertTitle>
          <AlertDescription>
            This page is for information purposes only and is not editable. 
            Organizational changes are made within Salesforce.
          </AlertDescription>
        </Alert>

        {/* Account Details */}
        <Card>
          <CardHeader>
            <CardTitle>Account Information</CardTitle>
          </CardHeader>
          <CardContent>
        
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Account Manager */}
              {account.account_manager_name && (
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-2">Account Manager</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-4">
                      <User className="h-5 w-5 text-muted-foreground flex-none" />
                      <p>{account.account_manager_name}</p>
                    </div>
                    {account.account_manager_email && (
                      <div className="flex items-center gap-4">
                        <Mail className="h-5 w-5 text-muted-foreground flex-none" />
                        <a 
                          href={`mailto:${account.account_manager_email}`}
                          className="text-primary hover:underline"
                        >
                          {account.account_manager_email}
                        </a>
                      </div>
                    )}
                    {account.account_manager_phone && (
                      <div className="flex items-center gap-4">
                        <Phone className="h-5 w-5 text-muted-foreground flex-none" />
                        <p>{account.account_manager_phone}</p>
                      </div>
                    )}
                    {account.account_manager_tz && (
                      <div className="flex items-center gap-4">
                        <Clock className="h-5 w-5 text-muted-foreground flex-none" />
                        <p>{account.account_manager_tz}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Other Details */}
              <div className="space-y-4">
                {account.salesforce_sfid && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Salesforce ID</h3>
                    <p>{account.salesforce_sfid}</p>
                  </div>
                )}
                {account.registration_slug && (
                  <div>
                    <h3 className="text-sm font-medium text-muted-foreground">Registration Slug</h3>
                    <p>{account.registration_slug}</p>
                  </div>
                )}
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground">SAML Login</h3>
                  <p>{account.uses_saml_login ? 'Enabled' : 'Disabled'}</p>
                </div>
              </div>
            </div>

            {/* Notes */}
            {account.notes && (
              <div className="mt-6">
                <h3 className="text-sm font-medium text-muted-foreground mb-2">Customer-Specific Notes</h3>
                <p className="whitespace-pre-wrap bg-gray-50 p-3 rounded-md">
                  {account.notes}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Licenses Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              Licensed Tools
            </CardTitle>
          </CardHeader>
          <CardContent>

            {!hasActiveLicenses ? (
              <Empty>
                <EmptyIcon variant="muted">
                  <AlertCircle className="h-8 w-8" />
                </EmptyIcon>
                <EmptyTitle>No Active Licenses</EmptyTitle>
                <EmptyDescription>
                  This account does not have any active licenses.
                </EmptyDescription>
              </Empty>
            ) : (
          <div className="space-y-4">
            {/* Current Tools */}
            {currentLicenses.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-2xl font-normal font-serif">Current Tools</h3>
              
                <div className="space-y-4">
                  {currentLicenses.map((license) => (
                    <LicenseRow
                      key={license.id}
                      license={license}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Legacy Tools */}
            {legacyLicenses.length > 0 && (
              <div className="space-y-4">
                  <h3 className="text-2xl font-normal font-serif">Legacy Tools</h3>
               
                <div className="space-y-4">
                  {legacyLicenses.map((license) => (
                    <LicenseRow
                      key={license.id}
                      license={license}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
            )}
          </CardContent>
        </Card>
      </ContentContainer>
    </>
  );
}

// License row component
function LicenseRow({ license }: { license: License }) {
  const isExpired = license.usage_end_date && new Date(license.usage_end_date) < new Date();
  const isActive = license.active && !isExpired;

  return (
    <Card size="medium" className="shadow-none border border-accent-sand">
      <CardContent>
 
    <div className="">
      <div className="flex items-start gap-4">
        {/* Status Indicator */}
        <div className="flex-shrink-0 mt-px">
          {isActive ? (
            <CheckCircle className="h-5 w-5 text-primary" />
          ) : (
            <XCircle className="h-5 w-5 text-destructive" />
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          {/* Product Name */}
          <div className="mb-4">
            <h4 className="font-normal text-xl mb-1 font-serif text-primary">
              {license.product_name}
            </h4>
          </div>

          {/* Seat Usage with Progress */}
          <div className="space-y-2 mb-4">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">
                {license.seats_used} / {license.total_seats} Seats Used
              </span>
              {license.seats_available > 0 && (
                <span className="text-sm italic">
                  {license.seats_available} Available
                </span>
              )}
            </div>
            <div className="relative w-full">
              <Progress 
                value={(license.seats_used / license.total_seats) * 100} 
                max={100}
                size="md"
              />
            </div>
          </div>

          {/* Divider */}
          <hr className="my-4" />

          {/* Additional Details */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-xs mb-1">Purchased Seats</p>
              <p className="font-medium">{license.seats_purchased}</p>
            </div>
            <div>
              <p className="text-xs mb-1">Complimentary Seats</p>
              <p className="font-medium">{license.seats_comped || 0}</p>
            </div>
            <div>
              <p className="text-xs mb-1">Auto-Assignment</p>
              <div className="mt-1">
                {license.allow_auto_assignment ? (
                  <Badge variant="success" tint>
                    Enabled
                  </Badge>
                ) : (
                  <Badge variant="warning" tint>
                    Disabled
                  </Badge>
                )}
              </div>
            </div>
            {license.usage_end_date ? (
              <div>
                <p className="text-xs mb-1">Expires</p>
                <p className={`font-medium ${isExpired ? 'text-destructive' : ''}`}>
                  {format(new Date(license.usage_end_date), 'MMM d, yyyy')}
                </p>
              </div>
            ) : (
              <div>
                <p className="text-xs mb-1">Expires</p>
                <p className="font-medium">No expiration</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </CardContent>
    </Card>
  );
}